import { getApiBaseUrl } from '../config/api.config'
import { getTokenFromUrl } from '../utils/auth'
import { configureDebugLogging, debug } from '../utils/debug'

import { CustomerApiError } from './customerApi'

import type { EnvironmentWithChina } from '../App'

/**
 * Address book — Customer API address sub-resource.
 *
 * | Method | Endpoint                                          |
 * | ------ | ------------------------------------------------- |
 * | GET    | `/Accounts/{accountNumber}/addresses`             |
 * | POST   | `/Accounts/{accountNumber}/addresses`             |
 * | PATCH  | `/Accounts/{accountNumber}/addresses/{addressId}` |
 * | DELETE | `/Accounts/{accountNumber}/addresses/{addressId}` |
 *
 * Design: https://suitsupply.atlassian.net/wiki/spaces/SAL/pages/5981405267/Customer+API+sync+and+address+book+design
 *
 * `isDefault` is sent on POST/PATCH rather than through the separate
 * `/set-default` endpoint — SFSC unsets the previous default itself, so one
 * write is enough and the list can never briefly show two primary rows.
 *
 * The whole sub-resource sits behind the backend's `AddressBook:Enabled` kill
 * switch and answers `404` while that is off, so callers must render the read
 * failure rather than assume an empty address book.
 */

/** One address as returned by `GET`. `addressId` is absent on unsaved forms. */
export interface CustomerAddress {
  addressId?: string
  firstName: string
  lastName: string
  company: string
  addressLine1: string
  addressLine2: string
  postalCode: string
  city: string
  /** US / Canada only — omitted from the payload for every other country. */
  state: string
  /** China only — omitted from the payload for every other country. */
  district: string
  countryCode: string
  phone: string
  /** The "Primary" address. Exactly one per account. */
  isDefault: boolean
}

function buildUrl(
  env: EnvironmentWithChina,
  accountNumber: string,
  addressId?: string,
): string {
  const base = `${getApiBaseUrl(env)}/public/customerapi/Accounts/${encodeURIComponent(accountNumber)}/addresses`
  return addressId ? `${base}/${encodeURIComponent(addressId)}` : base
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  url: string,
  authToken: string | undefined,
  body?: unknown,
): Promise<T> {
  const token = authToken || getTokenFromUrl()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  // Deliberately no `body`: it carries the customer's name, phone and full
  // address, and `debug` is live on tst/acc — enough to route-debug without
  // writing PII into the browser console.
  debug.log('[AddressAPI] Request:', { method, url, hasToken: !!token })

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers,
      mode: 'cors',
      credentials: 'omit',
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (error) {
    throw new CustomerApiError(
      `Network error while calling the address book: ${error instanceof Error ? error.message : 'Unknown error'}`,
    )
  }

  debug.log('[AddressAPI] Response status:', response.status)

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new CustomerApiError(
      `Address book request failed: ${response.statusText}`,
      response.status,
      errorText,
    )
  }

  // `DELETE` and `PATCH` answer with a small `{ result }` body, but an empty
  // `204` is just as valid a success — don't let `json()` turn that into an error.
  return (await response.json().catch(() => null)) as T
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * Widen one API row into the all-strings shape the form and the card formatter
 * expect.
 *
 * The gateway sends explicit `null`s (`"state": null`, `"addressLine2": null`)
 * and omits optional keys entirely, so every consumer would otherwise have to
 * null-guard before calling `.trim()` on them. Normalising once here is what
 * keeps `Address` honest as a `string` record.
 */
function normalizeAddress(row: Partial<CustomerAddress>): CustomerAddress {
  return {
    addressId: typeof row.addressId === 'string' ? row.addressId : undefined,
    firstName: text(row.firstName),
    lastName: text(row.lastName),
    company: text(row.company),
    addressLine1: text(row.addressLine1),
    addressLine2: text(row.addressLine2),
    postalCode: text(row.postalCode),
    city: text(row.city),
    state: text(row.state),
    district: text(row.district),
    countryCode: text(row.countryCode),
    phone: text(row.phone),
    isDefault: row.isDefault === true,
  }
}

export async function fetchAddresses(
  accountNumber: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<CustomerAddress[]> {
  configureDebugLogging(env)

  const data = await request<CustomerAddress[] | null>(
    'GET',
    buildUrl(env, accountNumber),
    authToken,
  )

  return Array.isArray(data) ? data.map(normalizeAddress) : []
}

export async function createAddress(
  accountNumber: string,
  address: CustomerAddress,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<void> {
  configureDebugLogging(env)
  await request('POST', buildUrl(env, accountNumber), authToken, address)
}

export async function updateAddress(
  accountNumber: string,
  addressId: string,
  address: CustomerAddress,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<void> {
  configureDebugLogging(env)
  await request(
    'PATCH',
    buildUrl(env, accountNumber, addressId),
    authToken,
    address,
  )
}

export async function deleteAddress(
  accountNumber: string,
  addressId: string,
  env: EnvironmentWithChina,
  authToken?: string,
): Promise<void> {
  configureDebugLogging(env)
  await request('DELETE', buildUrl(env, accountNumber, addressId), authToken)
}
